/**
 * CustomerScheduledJobs (SCHED-01) — the customer's Pegasus-scheduled jobs:
 * assigned tech + day + arrival window + call-ahead + the service type & fee.
 * Surfaced here so a job you just scheduled is visible on the record (Seth:
 * don't schedule something and then wonder where it went).
 *
 * Click a row to open its editable detail (PegasusJobPeek — set the exact time,
 * send it back to the queue, open the board). "Remove" cancels the booking with
 * a confirm (Seth 2026-06-23: the old "Unschedule" silently failed to refresh
 * the list, and the rows weren't openable).
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarCheck, Phone, Trash2, X } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import { useScheduledForCustomer, useEmpNames, useResolveTicket, type PendingTicket } from "@/features/board/queue";

const prettyDate = (d: string | null) =>
  d
    ? new Date(`${d}T00:00:00`).toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      })
    : "—";

export function CustomerScheduledJobs({ custno }: { custno: string }) {
  const navigate = useNavigate();
  const { data: jobs } = useScheduledForCustomer(custno);
  const empNames = useEmpNames().data ?? {};
  const resolve = useResolveTicket();
  const [confirmId, setConfirmId] = useState<string | null>(null);
  // Click a scheduled call → straight onto the dispatch board on its day, with
  // its full detail window auto-opened (Seth: bypass the compact peek entirely).
  // We hand the board the whole dispatch via router state so the window opens
  // INSTANTLY — the board floods its connection with queries on mount, so a
  // by-id refetch there can lag ~10s; passing the object we already have avoids
  // the wait. The `?focus=` param stays for shareable/refreshable deep links.
  const openOnBoard = (j: PendingTicket) =>
    navigate(
      j.scheduled_date ? `/board?date=${j.scheduled_date}&focus=${j.id}` : `/board?focus=${j.id}`,
      { state: { focusJob: j } },
    );
  if (!jobs || jobs.length === 0) return null;

  return (
    <Card className="p-4">
      <div className="mb-2.5 flex items-center gap-2">
        <CalendarCheck className="h-4 w-4 text-emerald-600" />
        <h3 className="text-sm font-semibold text-slate-900">Scheduled</h3>
        <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700">
          {jobs.length}
        </span>
      </div>
      <ul className="space-y-2">
        {jobs.map((j) => (
          <li
            key={j.id}
            role="button"
            tabIndex={0}
            onClick={() => openOnBoard(j)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openOnBoard(j); } }}
            title="Open this call on the dispatch board"
            className="cursor-pointer rounded-lg border border-hairline p-3 transition-colors hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800">
                  {j.service_type_name ?? j.summary}
                </div>
                <div className="mt-0.5 text-xs text-slate-600">
                  {j.assigned_empno
                    ? (empNames[j.assigned_empno] ?? j.assigned_empno)
                    : "Unassigned"}{" "}
                  · {prettyDate(j.scheduled_date)}
                  {j.scheduled_window ? ` · ${j.scheduled_window}` : ""}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  {j.call_ahead && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600">
                      <Phone className="h-3 w-3" /> {j.call_ahead_method === "text" ? "Text" : "Call"} on the way
                      {j.call_ahead_phone ? ` · ${j.call_ahead_name ? `${j.call_ahead_name} ` : ""}${j.call_ahead_phone}` : ""}
                    </span>
                  )}
                  {j.diag_fee != null && (
                    <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600">
                      ${j.diag_fee}
                    </span>
                  )}
                  {j.urgency && j.urgency !== "routine" && (
                    <span
                      className={cn(
                        "rounded-full px-1.5 py-0.5 text-[11px] font-semibold",
                        j.urgency === "asap"
                          ? "bg-red-50 text-red-700"
                          : "bg-amber-50 text-amber-700",
                      )}
                    >
                      {j.urgency}
                    </span>
                  )}
                </div>
              </div>

              {/* Remove (cancel the booking) — guarded by a confirm; stops the row's open-peek. */}
              <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                {confirmId === j.id ? (
                  <span className="inline-flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => { resolve.mutate({ id: j.id, status: "cancelled" }); setConfirmId(null); }}
                      disabled={resolve.isPending}
                      className="rounded-md bg-rose-600 px-2 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-rose-700 disabled:opacity-50"
                    >
                      Remove
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmId(null)}
                      title="Keep it"
                      className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmId(j.id)}
                    title="Remove this scheduled call"
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-slate-500 transition-colors hover:bg-rose-50 hover:text-rose-600"
                  >
                    <Trash2 className="h-3 w-3" /> Remove
                  </button>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
